import { Suspense, lazy, type ComponentProps, type RefObject } from "react";
import LandingDashboard from "../../components/chat/LandingDashboard";
import EmptyChannelNotice from "../../components/chat/EmptyChannelNotice";
import { MessageListVirtualized } from "../../components/chat/MessageListVirtualized";
import { MessageListSkeleton } from "../../components/ui/skeletons";
import { useMediaLightbox } from "../../hooks/useMediaLightbox";
import type { AuthUser } from "../../types/auth";
import type { Msg } from "../../types/chat";
import type { Channel } from "./chatTypes";
import type { GalleryChannelViewProps } from "./GalleryChannelView";

// Each special channel type is loaded the first time someone opens one.
const KitchenChannelView = lazy(() => import("../../components/kitchen/KitchenChannelView"));
const CalendarChannelView = lazy(() => import("../../components/calendar/CalendarChannelView"));
const HabitChannelView = lazy(() => import("../../components/habits/HabitChannelView"));
const ChallengeChannelView = lazy(() => import("../../components/challenges/ChallengeChannelView"));
const KanbanChannelView = lazy(() => import("../../components/kanban/KanbanChannelView"));
const FormChannelView = lazy(() => import("../../components/forms/FormChannelView"));
const NotesChannelView = lazy(() => import("../../components/notes/NotesChannelView"));
const GalleryChannelView = lazy(() => import("./GalleryChannelView").then((m) => ({ default: m.GalleryChannelView })));
import type { useKanbanData } from "./hooks/useKanbanData";
import type { useNotesActions } from "./hooks/useNotesActions";
import type { useUnread } from "./hooks/useUnread";

type MemberList = { id: string; name: string; username?: string; avatarUrl?: string | null; status?: string; nameColor?: string | null; role?: string; bio?: string; roles?: { id: string; name: string; color?: string | null }[] }[];

/** What fills the scrolling pane: landing dashboard, a special channel view, the gallery, notes, or the message list. Pure presentation: all state stays in ChatLayout. */
export type ChannelBodyProps = {
  currentVoidId: string;
  currentChannelId: string;
  currentChannelType: string;
  currentChannel: Channel | undefined;
  currentChannelFqid: string | null;
  fq: (vId: string, cId: string) => string;
  msgs: Msg[];
  /** The channel's history hasn't arrived yet. */
  messagesLoading: boolean;
  channels: Channel[];
  setChannels: GalleryChannelViewProps["setChannels"];
  members: MemberList;
  me: ComponentProps<typeof LandingDashboard>["me"];
  user: AuthUser | null;
  listRef: RefObject<HTMLDivElement | null>;
  isMdUp: ComponentProps<typeof LandingDashboard>["isMdUp"];
  channelPaneView: ComponentProps<typeof LandingDashboard>["channelPaneView"];
  activeDmVoids: ComponentProps<typeof LandingDashboard>["activeDmVoids"];
  dmFriendSearch: ComponentProps<typeof LandingDashboard>["dmFriendSearch"];
  setDmFriendSearch: ComponentProps<typeof LandingDashboard>["setDmFriendSearch"];
  filteredFriendsForDmSearch: ComponentProps<typeof LandingDashboard>["filteredFriendsForDmSearch"];
  dmStartBusyUserId: ComponentProps<typeof LandingDashboard>["dmStartBusyUserId"];
  unread: ComponentProps<typeof LandingDashboard>["unread"];
  getDmDisplayName: ComponentProps<typeof LandingDashboard>["getDmDisplayName"];
  landingDateLabel: ComponentProps<typeof LandingDashboard>["landingDateLabel"];
  landingGreeting: ComponentProps<typeof LandingDashboard>["landingGreeting"];
  userStatusMessage: ComponentProps<typeof LandingDashboard>["userStatusMessage"];
  onUpdateStatus?: ComponentProps<typeof LandingDashboard>["onUpdateStatus"];
  friendReqCount: ComponentProps<typeof LandingDashboard>["friendReqCount"];
  landingSummaryStats: ComponentProps<typeof LandingDashboard>["landingSummaryStats"];
  landingSearchPanel: ComponentProps<typeof LandingDashboard>["landingSearchPanel"];
  friendsOnline: ComponentProps<typeof LandingDashboard>["friendsOnline"];
  mobileFriendSlots: ComponentProps<typeof LandingDashboard>["mobileFriendSlots"];
  globalUserIds: ComponentProps<typeof LandingDashboard>["globalUserIds"];
  presenceFromStatus: ComponentProps<typeof LandingDashboard>["presenceFromStatus"];
  nonDmVoids: ComponentProps<typeof LandingDashboard>["nonDmVoids"];
  landingFavoriteCards: ComponentProps<typeof LandingDashboard>["landingFavoriteCards"];
  startDmWithUser: (userId: string) => unknown;
  switchVoid: (id: string) => void;
  switchChannel: (id: string) => void;
  handleOpenProfile: ComponentProps<typeof LandingDashboard>["onOpenProfile"];
  setFriendsOpen: (open: boolean) => void;
  createSpace: () => void;
  currentSharedItems: ComponentProps<typeof KitchenChannelView>["sharedItems"];
  onOpenSettings: ComponentProps<typeof CalendarChannelView>["onOpenSettings"];
  askInput: NonNullable<ComponentProps<typeof HabitChannelView>["askInput"]>;
  currentKanbanFqid: string | null;
  currentKanbanLists: ComponentProps<typeof KanbanChannelView>["lists"];
  currentKanbanTags: ComponentProps<typeof KanbanChannelView>["channelTags"];
  currentChannelTags: ComponentProps<typeof KanbanChannelView>["channelTags"];
  mutateKanbanLists: ReturnType<typeof useKanbanData>["mutateKanbanLists"];
  mutateChannelTags: ReturnType<typeof useKanbanData>["mutateChannelTags"];
  fetchChannelTags: ReturnType<typeof useKanbanData>["fetchChannelTags"];
  isListFav: ComponentProps<typeof KanbanChannelView>["isListFav"];
  toggleListFav: ComponentProps<typeof KanbanChannelView>["toggleListFav"];
  createNote: ReturnType<typeof useNotesActions>["createNote"];
  updateNote: ReturnType<typeof useNotesActions>["updateNote"];
  deleteNote: ReturnType<typeof useNotesActions>["deleteNote"];
  galSelModeByChan: GalleryChannelViewProps["galSelModeByChan"];
  setGalSelModeByChan: GalleryChannelViewProps["setGalSelModeByChan"];
  galSelectedByChan: GalleryChannelViewProps["galSelectedByChan"];
  setGalSelectedByChan: GalleryChannelViewProps["setGalSelectedByChan"];
  galVisibleCountByChan: GalleryChannelViewProps["galVisibleCountByChan"];
  setGalVisibleCountByChan: GalleryChannelViewProps["setGalVisibleCountByChan"];
  imgLoadedByUrl: GalleryChannelViewProps["imgLoadedByUrl"];
  setImgLoadedByUrl: GalleryChannelViewProps["setImgLoadedByUrl"];
  galleryFileRef: GalleryChannelViewProps["galleryFileRef"];
  addGalleryFiles: GalleryChannelViewProps["addGalleryFiles"];
  setImagePreview: ReturnType<typeof useMediaLightbox>["setImagePreview"];
  setImagePreviewList: ReturnType<typeof useMediaLightbox>["setImagePreviewList"];
  setImagePreviewIndex: ReturnType<typeof useMediaLightbox>["setImagePreviewIndex"];
  setVideoPreview: ReturnType<typeof useMediaLightbox>["setVideoPreview"];
  unreadMarkerByChan: ReturnType<typeof useUnread>["unreadMarkerByChan"];
  hasMoreByChan: Record<string, boolean>;
  isMine: ComponentProps<typeof MessageListVirtualized>["isMine"];
  pickerFor: ComponentProps<typeof MessageListVirtualized>["pickerFor"];
  setPickerFor: ComponentProps<typeof MessageListVirtualized>["setPickerFor"];
  reactionBtnRef: ComponentProps<typeof MessageListVirtualized>["reactionBtnRef"];
  reactionMenuRef: ComponentProps<typeof MessageListVirtualized>["reactionMenuRef"];
  setReplyTo: ComponentProps<typeof MessageListVirtualized>["setReplyTo"];
  startEdit: ComponentProps<typeof MessageListVirtualized>["startEdit"];
  editingId: ComponentProps<typeof MessageListVirtualized>["editingId"];
  editText: ComponentProps<typeof MessageListVirtualized>["editText"];
  setEditText: ComponentProps<typeof MessageListVirtualized>["setEditText"];
  saveEdit: ComponentProps<typeof MessageListVirtualized>["saveEdit"];
  cancelEdit: ComponentProps<typeof MessageListVirtualized>["cancelEdit"];
  toggleReaction: ComponentProps<typeof MessageListVirtualized>["toggleReaction"];
  revealedSpoilers: ComponentProps<typeof MessageListVirtualized>["revealedSpoilers"];
  setRevealedSpoilers: ComponentProps<typeof MessageListVirtualized>["setRevealedSpoilers"];
  linkifyMessage: ComponentProps<typeof MessageListVirtualized>["linkifyMessage"];
  linkPreviewMap: ComponentProps<typeof MessageListVirtualized>["linkPreviewMap"];
  renderLinkPreview: ComponentProps<typeof MessageListVirtualized>["renderLinkPreview"];
  hoverReaction: ComponentProps<typeof MessageListVirtualized>["hoverReaction"];
  setHoverReaction: ComponentProps<typeof MessageListVirtualized>["setHoverReaction"];
  reactionTipPos: ComponentProps<typeof MessageListVirtualized>["reactionTipPos"];
  setReactionTipPos: ComponentProps<typeof MessageListVirtualized>["setReactionTipPos"];
  reactionInfoByMsg: ComponentProps<typeof MessageListVirtualized>["reactionInfoByMsg"];
  loadReactionsFor: ComponentProps<typeof MessageListVirtualized>["loadReactionsFor"];
  seenTip: ComponentProps<typeof MessageListVirtualized>["seenTip"];
  setSeenTip: ComponentProps<typeof MessageListVirtualized>["setSeenTip"];
  timeAgo: ComponentProps<typeof MessageListVirtualized>["timeAgo"];
  formatTime: ComponentProps<typeof MessageListVirtualized>["formatTime"];
  handleDeleteMessage: ComponentProps<typeof MessageListVirtualized>["onDeleteMessage"];
  getQuickEmojis: () => string[];
  chatScrollToId: ComponentProps<typeof MessageListVirtualized>["scrollToId"];
  chatHighlightId: ComponentProps<typeof MessageListVirtualized>["highlightMsgId"];
};

export function ChannelBody({
  currentVoidId, currentChannelId, currentChannelType, currentChannel, currentChannelFqid, fq, msgs, messagesLoading, channels, setChannels, members, me, user, listRef, isMdUp, channelPaneView, activeDmVoids, dmFriendSearch, setDmFriendSearch, filteredFriendsForDmSearch, dmStartBusyUserId, unread, getDmDisplayName, landingDateLabel, landingGreeting, userStatusMessage, onUpdateStatus, friendReqCount, landingSummaryStats, landingSearchPanel, friendsOnline, mobileFriendSlots, globalUserIds, presenceFromStatus, nonDmVoids, landingFavoriteCards, startDmWithUser, switchVoid, switchChannel, handleOpenProfile, setFriendsOpen, createSpace, currentSharedItems, onOpenSettings, askInput, currentKanbanFqid, currentKanbanLists, currentKanbanTags, currentChannelTags, mutateKanbanLists, mutateChannelTags, fetchChannelTags, isListFav, toggleListFav, createNote, updateNote, deleteNote, galSelModeByChan, setGalSelModeByChan, galSelectedByChan, setGalSelectedByChan, galVisibleCountByChan, setGalVisibleCountByChan, imgLoadedByUrl, setImgLoadedByUrl, galleryFileRef, addGalleryFiles, setImagePreview, setImagePreviewList, setImagePreviewIndex, setVideoPreview, unreadMarkerByChan, hasMoreByChan, isMine, pickerFor, setPickerFor, reactionBtnRef, reactionMenuRef, setReplyTo, startEdit, editingId, editText, setEditText, saveEdit, cancelEdit, toggleReaction, revealedSpoilers, setRevealedSpoilers, linkifyMessage, linkPreviewMap, renderLinkPreview, hoverReaction, setHoverReaction, reactionTipPos, setReactionTipPos, reactionInfoByMsg, loadReactionsFor, seenTip, setSeenTip, timeAgo, formatTime, handleDeleteMessage, getQuickEmojis, chatScrollToId, chatHighlightId
}: ChannelBodyProps) {
  return (
    <Suspense fallback={<div className="p-6 text-sm text-fg-3">Loading…</div>}>
          {!currentVoidId ? (
            <LandingDashboard
              isMdUp={isMdUp}
              channelPaneView={channelPaneView}
              activeDmVoids={activeDmVoids}
              dmFriendSearch={dmFriendSearch}
              setDmFriendSearch={setDmFriendSearch}
              filteredFriendsForDmSearch={filteredFriendsForDmSearch}
              dmStartBusyUserId={dmStartBusyUserId}
              unread={unread}
              getDmDisplayName={getDmDisplayName}
              landingDateLabel={landingDateLabel}
              landingGreeting={landingGreeting}
              me={me}
              userName={user?.username}
              userStatusMessage={userStatusMessage}
              onUpdateStatus={onUpdateStatus}
              friendReqCount={friendReqCount}
              landingSummaryStats={landingSummaryStats}
              landingSearchPanel={landingSearchPanel}
              friendsOnline={friendsOnline}
              mobileFriendSlots={mobileFriendSlots}
              globalUserIds={globalUserIds}
              presenceFromStatus={presenceFromStatus}
              nonDmVoids={nonDmVoids}
              landingFavoriteCards={landingFavoriteCards}
              onStartDm={(id) => { void startDmWithUser(id); }}
              onOpenDm={(id) => { switchVoid(id); switchChannel('chat'); }}
              onOpenProfile={handleOpenProfile}
              onOpenFriends={() => setFriendsOpen(true)}
              onCreateSpace={createSpace}
            />
      ) : currentChannelType === 'kitchen' ? (
            <KitchenChannelView
              channelFqid={currentChannelFqid || fq(currentVoidId, currentChannelId)}
              sharedItems={currentSharedItems}
            />
          ) : currentChannelType === 'calendar' ? (
            <CalendarChannelView
              fid={currentChannelFqid || fq(currentVoidId, currentChannelId)}
              onOpenSettings={onOpenSettings}
              meId={me.userId || (user as any)?.userId || null}
              members={members}
            />
          ) : currentChannelType === 'habit' ? (
            <HabitChannelView fid={fq(currentVoidId, currentChannelId)} members={members} meId={me.userId || null} askInput={askInput} />
          ) : currentChannelType === 'challenge' ? (
            <ChallengeChannelView fid={currentChannelFqid || fq(currentVoidId, currentChannelId)} meId={me.userId || null} members={members} />
          ) : currentChannelType === 'kanban' ? (
            <KanbanChannelView
              fid={currentKanbanFqid || fq(currentVoidId, currentChannelId)}
              lists={currentKanbanLists}
              mutateLists={(updater) => mutateKanbanLists(currentKanbanFqid || fq(currentVoidId, currentChannelId), updater)}
              channelTags={currentKanbanTags}
              mutateTags={(updater) => mutateChannelTags(currentKanbanFqid || fq(currentVoidId, currentChannelId), updater)}
              askInput={askInput}
              currentVoidId={currentVoidId}
              currentChannelId={currentChannelId}
              isListFav={isListFav}
              toggleListFav={toggleListFav}
            />
          ) : currentChannelType === 'form' ? (
            <FormChannelView
              fid={currentChannelFqid || fq(currentVoidId, currentChannelId)}
              members={members}
              meId={me.userId || null}
              channelTags={currentChannelTags}
              mutateTags={(updater) => mutateChannelTags(currentChannelFqid || fq(currentVoidId, currentChannelId), updater)}
              refreshTags={() => fetchChannelTags(currentChannelFqid || fq(currentVoidId, currentChannelId), { force: true })}
            />
          ) : (
          (() => {
            if (currentChannelType === 'voice') {
              return null;
            }
              if (currentChannelType === 'gallery') {
                return (
                  <GalleryChannelView
                    msgs={msgs}
                    channels={channels}
                    setChannels={setChannels}
                    currentVoidId={currentVoidId}
                    currentChannelId={currentChannelId}
                    fq={fq}
                    galSelModeByChan={galSelModeByChan}
                    setGalSelModeByChan={setGalSelModeByChan}
                    galSelectedByChan={galSelectedByChan}
                    setGalSelectedByChan={setGalSelectedByChan}
                    galVisibleCountByChan={galVisibleCountByChan}
                    setGalVisibleCountByChan={setGalVisibleCountByChan}
                    imgLoadedByUrl={imgLoadedByUrl}
                    setImgLoadedByUrl={setImgLoadedByUrl}
                    revealedSpoilers={revealedSpoilers}
                    setRevealedSpoilers={setRevealedSpoilers}
                    setImagePreview={setImagePreview}
                    setImagePreviewList={setImagePreviewList}
                    setImagePreviewIndex={setImagePreviewIndex}
                    galleryFileRef={galleryFileRef}
                    addGalleryFiles={addGalleryFiles}
                  />
                );
              }
            if (currentChannelType === 'notes') {
              const noteMessages = msgs.map((m) => ({ id: m.id, content: String(m.content || ''), createdAt: m.createdAt || '' }));
              return (
                <NotesChannelView
                  fid={fq(currentVoidId, currentChannelId)}
                  notes={noteMessages}
                  channelTags={currentChannelTags}
                  mutateTags={(updater) => mutateChannelTags(fq(currentVoidId, currentChannelId), updater)}
                  refreshTags={() => fetchChannelTags(fq(currentVoidId, currentChannelId), { force: true })}
                  onCreateNote={createNote}
                  onUpdateNote={updateNote}
                  onDeleteNote={deleteNote}
                />
              );
            }
            /* --- Chat message rendering (legacy; candidate to split into MessageList component) --- */
            const cid = fq(currentVoidId, currentChannelId);
            const markId = unreadMarkerByChan[cid] || null;
            if (messagesLoading) return <MessageListSkeleton />;
            const isEmptyChannel = msgs.length === 0 && !hasMoreByChan[cid];
            return (
              <>
              {isEmptyChannel && (
                <EmptyChannelNotice channelType={currentChannelType} channelName={currentChannel?.name || currentChannelId} />
              )}
              <MessageListVirtualized
                messages={msgs}
                markId={markId}
                cid={cid}
                scrollParent={listRef.current || undefined}
                isMine={isMine}
                pickerFor={pickerFor}
                setPickerFor={setPickerFor}
                reactionBtnRef={reactionBtnRef}
                reactionMenuRef={reactionMenuRef}
                setReplyTo={setReplyTo}
                startEdit={startEdit}
                editingId={editingId}
                editText={editText}
                setEditText={setEditText}
                saveEdit={saveEdit}
                cancelEdit={cancelEdit}
                toggleReaction={toggleReaction}
                revealedSpoilers={revealedSpoilers}
                setRevealedSpoilers={setRevealedSpoilers}
                setImagePreview={setImagePreview}
                setImagePreviewList={setImagePreviewList}
                setImagePreviewIndex={setImagePreviewIndex}
                setVideoPreview={setVideoPreview}
                linkifyMessage={linkifyMessage}
                linkPreviewMap={linkPreviewMap}
                renderLinkPreview={renderLinkPreview}
                members={members}
                hoverReaction={hoverReaction}
                setHoverReaction={setHoverReaction}
                reactionTipPos={reactionTipPos}
                setReactionTipPos={setReactionTipPos}
                reactionInfoByMsg={reactionInfoByMsg}
                loadReactionsFor={loadReactionsFor}
                seenTip={seenTip}
                setSeenTip={setSeenTip}
                timeAgo={timeAgo}
                formatTime={formatTime}
                onDeleteMessage={handleDeleteMessage}
                quickEmojis={getQuickEmojis()}
                scrollToId={chatScrollToId}
                highlightMsgId={chatHighlightId}
              />
              </>
            );
          })()
          )}
    </Suspense>
  );
}
